"use client";
import { IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import Link from "next/link";
import AdminPopover from "../../content/AdminPopover";
import { shadow } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import BlockToggle from "./tabs/toggle";
import TextOptions from "./tabs/textblock/options";
import TextCustomization from "./tabs/textblock/customization";
import BlockMetadata from "./tabs/metadata";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    theme: ITheme;
    newBlock?: string | null;
}
export default function BlockText({
    admin,
    user,
    block,
    update,
    style,
    theme,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Text",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <TextOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <TextCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <Separator />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={blockUpdate.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={
                "TextBlock | " + blockUpdate.options?.content ||
                blockUpdate.message
            }
            isOpen={isExpanded}
            onClose={handleClose}
        >
            <p
                style={style}
                id="textblock"
                className={`{formatClassName(text)} 
                ${shadow(theme.button_shadow)}
                ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                w-full 
                select-none 
                overflow-hidden text-ellipsis
                whitespace-pre-wrap text-wrap
                px-1 py-3 
                text-center text-xl
                font-bold
                shadow-md transition-all
                 duration-150 duration-150
                ${blockUpdate.name.replaceAll(" ", "-")}
                `}
            >
                {blockUpdate.options?.content || blockUpdate.message}
            </p>
        </AdminPopover>
    );
}
